import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { ClockCountdown, IdentificationCard, CopySimple, Sliders } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function AgingCard({ aging }) {
  if (!aging) return null;
  const B = [["current", "Current"], ["d1_30", "1–30d"], ["d31_60", "31–60d"], ["d61_90", "61–90d"], ["d90_plus", "90d+"]];
  return (
    <div className="dc-card overflow-hidden" data-testid="ap-aging-card">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <ClockCountdown size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Supplier Aging</h3>
        <span className="ml-auto font-mono-alt text-xs tabular-nums font-semibold" data-testid="ap-aging-total">Total {formatCurrency(aging.totals.total)}</span>
      </div>
      {aging.rows.length === 0 ? (
        <div className="px-5 py-5 text-sm text-slate-500">Nothing outstanding — all supplier bills are settled.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[860px]" data-testid="ap-aging-table">
            <thead><tr><th>Vendor</th>{B.map(([k, l]) => <th key={k} className="text-right">{l}</th>)}<th className="text-right">Total</th></tr></thead>
            <tbody>
              {aging.rows.map((r) => (
                <tr key={r.vendor} data-testid={`ap-aging-row-${r.vendor.replace(/\s+/g, "-")}`}>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900">{r.vendor}</td>
                  {B.map(([k]) => <td key={k} className={`text-right font-mono-alt text-xs tabular-nums ${k === "d90_plus" && r[k] > 0 ? "text-rose-600 font-semibold" : ""}`}>{r[k] ? formatCurrency(r[k]) : "—"}</td>)}
                  <td className="text-right font-mono-alt text-xs tabular-nums font-bold">{formatCurrency(r.total)}</td>
                </tr>
              ))}
              <tr className="font-bold border-t-2 border-slate-300">
                <td className="px-5 py-2 text-xs">TOTALS</td>
                {B.map(([k]) => <td key={k} className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(aging.totals[k])}</td>)}
                <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(aging.totals.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatementCard({ vendors }) {
  const [vendor, setVendor] = useState("");
  const [stmt, setStmt] = useState(null);
  const load = async (v) => {
    setVendor(v);
    try { const { data } = await api.get("/finance/ap/supplier-statement", { params: { vendor_name: v } }); setStmt(data); }
    catch (e) { toast.error(formatApiError(e)); setStmt(null); }
  };
  return (
    <div className="dc-card overflow-hidden" data-testid="ap-statement-card">
      <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap items-center gap-2">
        <IdentificationCard size={18} weight="fill" className="text-indigo-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Supplier Statement</h3>
        <div className="ml-auto w-64">
          <Select value={vendor || undefined} onValueChange={load}>
            <SelectTrigger className="h-8" data-testid="ap-statement-vendor"><SelectValue placeholder="Choose supplier…" /></SelectTrigger>
            <SelectContent>{vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {!stmt ? (
        <div className="px-5 py-5 text-sm text-slate-500">Pick a supplier to see their chronological statement with running balance.</div>
      ) : (
        <>
          <div className="responsive-table-wrap max-h-[320px] overflow-y-auto">
            <table className="data-table w-full min-w-[820px]" data-testid="ap-statement-table">
              <thead><tr><th>Date</th><th>Type</th><th>Ref</th><th>Description</th><th className="text-right">Charge</th><th className="text-right">Payment</th><th className="text-right">Balance</th></tr></thead>
              <tbody>
                {stmt.events.map((e, i) => (
                  <tr key={i}>
                    <td className="px-5 py-2 font-mono-alt text-xs">{formatDate(e.date)}</td>
                    <td><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${e.type === "bill" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{e.type}</span></td>
                    <td className="font-mono-alt text-xs">{e.ref}</td>
                    <td className="text-xs text-slate-600 max-w-[200px] truncate">{e.description}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums">{e.charge ? formatCurrency(e.charge) : "—"}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums">{e.payment ? formatCurrency(e.payment) : "—"}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums font-semibold">{formatCurrency(e.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 px-5 py-2 text-xs font-mono-alt tabular-nums" data-testid="ap-statement-closing">
            Billed {formatCurrency(stmt.total_billed)} · Paid {formatCurrency(stmt.total_paid)} · <b>Closing balance {formatCurrency(stmt.closing_balance)}</b>
          </div>
        </>
      )}
    </div>
  );
}

function DuplicatesCard({ dupes }) {
  if (!dupes) return null;
  return (
    <div className="dc-card overflow-hidden" data-testid="ap-duplicates-card">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <CopySimple size={18} weight="fill" className="text-amber-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Potential Duplicate Invoices</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{dupes.total} group(s) flagged</span>
      </div>
      {dupes.groups.length === 0 ? (
        <div className="px-5 py-5 text-sm text-slate-500" data-testid="ap-duplicates-empty">No potential duplicates detected (same vendor+amount within 45 days, or same bill number).</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {dupes.groups.slice(0, 10).map((g, i) => (
            <div key={i} className="px-5 py-3" data-testid={`ap-dupe-group-${i}`}>
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">
                {g.kind === "same_bill_number" ? `Same bill number · ${g.bill_number}` : `Same vendor + amount · ${g.vendor} · ${formatCurrency(g.amount)}`}
              </div>
              <div className="flex flex-wrap gap-2">
                {g.bills.map((b) => (
                  <span key={b.id} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono-alt text-[11px]">
                    {b.bill_number || b.id.slice(0, 8)} · {formatDate(b.bill_date)} · {formatCurrency(b.total)} · {b.status}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TolerancesCard({ isAdmin }) {
  const [tol, setTol] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/finance/ap/tolerances").then(({ data }) => setTol(data)).catch(() => {});
  }, []);
  if (!tol) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/finance/ap/tolerances", {
        total_pct: parseFloat(tol.total_pct) || 0, price_pct: parseFloat(tol.price_pct) || 0,
        qty_pct: parseFloat(tol.qty_pct) || 0, tax_pct: parseFloat(tol.tax_pct) || 0 });
      setTol(data); toast.success("Tolerances updated — the 3-way match uses them immediately");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="dc-card overflow-hidden" data-testid="ap-tolerances-card">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Sliders size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">3-Way Match Tolerances</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Mismatches beyond tolerance block payment unless an exception is approved</span>
      </div>
      <div className="flex flex-wrap items-end gap-3 px-5 py-4">
        {[["total_pct", "Total %"], ["price_pct", "Price %"], ["qty_pct", "Qty %"], ["tax_pct", "Tax %"]].map(([k, l]) => (
          <div key={k}>
            <Label className="text-xs">{l}</Label>
            <Input type="number" min="0" max="50" step="0.5" className="h-8 w-24" disabled={!isAdmin}
              value={tol[k]} onChange={(e) => setTol({ ...tol, [k]: e.target.value })} data-testid={`ap-tol-${k}`} />
          </div>
        ))}
        {isAdmin && <Button size="sm" className="btn-primary-blue h-8" onClick={save} disabled={busy} data-testid="ap-tol-save">Save</Button>}
      </div>
    </div>
  );
}

export default function APAdvancedSection({ isAdmin }) {
  const [aging, setAging] = useState(null);
  const [dupes, setDupes] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/finance/ap/aging").catch(() => ({ data: null })),
      api.get("/finance/ap/duplicates").catch(() => ({ data: null })),
    ]).then(([a, d]) => { setAging(a.data); setDupes(d.data); });
  }, []);
  const vendors = (aging?.rows || []).map((r) => r.vendor);
  return (
    <div className="space-y-5" data-testid="ap-advanced-section">
      <AgingCard aging={aging} />
      <StatementCard vendors={vendors} />
      <DuplicatesCard dupes={dupes} />
      <TolerancesCard isAdmin={isAdmin} />
    </div>
  );
}
